import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { SharedComponentsModule } from "@/components/shared/shared-components.module";
import { DropdownModule } from "primeng/dropdown";
import { RadioOption } from "@/components/shared/radio-list/radio-list.component";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { AssessmentService, FlightService, UserService } from "@/_services";
import { combineLatest, Observable, of, Subscription } from "rxjs";
import {
    AsyncPipe,
    CommonModule,
    DatePipe,
    NgTemplateOutlet,
} from "@angular/common";
import { DateTimeFormat } from "@/_utility/DateTimeFormat";
import {
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    take,
    tap,
} from "rxjs/operators";
import { ChartData } from "chart.js";
import { Options } from "chartjs-plugin-datalabels/types/options";
import { PercentChangePipe } from "@/_pipes/nan-fix.pipe";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { PerformanceChartComponent } from "@/components/home/home-crew-performance/performance-chart/performance-chart.component";
import moment from "moment";
import { StatsSummary } from "@/components/shared/graphics/graphic-interfaces";

const lightChartLabelColor = "var(--chart-text)";

const labelOptions: Options = {
    color: lightChartLabelColor,
    font: {
        weight: "bold",
        size: 14,
    },
    display: (context) => {
        const point = context.dataset.data[context.dataIndex] as number;
        return point > 0;
    },
};

@UntilDestroy()
@Component({
    selector: "app-home-crew-performance",
    standalone: true,
    imports: [
        CommonModule,
        SharedComponentsModule,
        DropdownModule,
        FormsModule,
        ReactiveFormsModule,
        AsyncPipe,
        DatePipe,
        NgTemplateOutlet,
        PerformanceChartComponent,
    ],
    templateUrl: "./home-crew-performance.component.html",
    styleUrl: "./home-crew-performance.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeCrewPerformanceComponent implements OnInit, OnDestroy {
    private flightService = inject(FlightService);
    private AssessmentService = inject(AssessmentService);
    private userService = inject(UserService);

    @Input() isAdmin: boolean = false;

    readonly DateTimeFormat = DateTimeFormat;
    timeFrameList: RadioOption[] = [
        { label: "Past 7 Days", value: 7 },
        { label: "Past 30 Days", value: 30 },
        { label: "Past 60 Days", value: 60 },
        { label: "Past 90 Days", value: 90 },
        { label: "Past Year", value: 365 },
    ];
    timeFrameControl: FormControl<number> = new FormControl<number>(30);
    riskAssessmentOptions$: Observable<{ label: string; value: string }[]>;
    riskAssessmentControl = new FormControl(null);
    timeFrameSubscription: Subscription;

    stats$: Observable<any>;

    readonly ChartOptions = {
        type: "line",
        plugins: {
            datalabels: labelOptions,
        },
        legend: { display: false },
        aspectRatio: 20,
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 5,
                bottom: 5,
                left: 5,
                right: 5,
            },
        },
        scales: {
            yAxes: [
                {
                    ticks: {
                        callback: function (value, index, values) {
                            return index === 0 || index === values.length - 1
                                ? value
                                : "";
                        },
                        beginAtZero: true,
                        stepSize: 5,
                        autoSkip: false,
                        display: false,
                    },
                },
            ],
            xAxes: [
                {
                    ticks: {
                        display: false, // ⬅️ Esto oculta los labels del eje X
                    },
                },
            ],
        },
    };
    averageScoreDataSet$: Observable<StatsSummary>;
    completionDataset$: Observable<StatsSummary>;

    ngOnInit() {
        this.riskAssessmentOptions$ =
            this.AssessmentService.getAssessmentsByType("assessment").pipe(
                untilDestroyed(this),
                map((assessments) => {
                    return assessments.map((assessment) => {
                        return {
                            label: assessment.title,
                            value: assessment._id,
                        };
                    });
                }),
                tap((options) => {
                    if (
                        !this.riskAssessmentControl.value &&
                        options.length > 0
                    ) {
                        this.riskAssessmentControl.setValue(options[0].value);
                    }
                }),
                shareReplay(1),
            );
        this.stats$ = combineLatest([
            this.timeFrameControl.valueChanges.pipe(
                startWith(this.timeFrameControl.value),
            ),
            this.riskAssessmentControl.valueChanges.pipe(
                startWith(this.riskAssessmentControl.value),
            ),
        ]).pipe(
            untilDestroyed(this),
            switchMap(([timeFrameValue, formType]) => {
                const filterParams = new URLSearchParams();

                if (timeFrameValue) {
                    const today = moment();
                    const startDate = moment().subtract(
                        timeFrameValue * 2,
                        "days",
                    );
                    filterParams.append(
                        "flightDate",
                        startDate.format("YYYY-MM-DD"),
                    );
                    filterParams.append(
                        "flightDate",
                        today.format("YYYY-MM-DD"),
                    );
                }

                // Always include formType - use current value or get first option
                let formTypeToUse = formType;
                if (!formTypeToUse) {
                    // If no formType, wait for options to load and use the first one
                    return this.riskAssessmentOptions$.pipe(
                        take(1),
                        switchMap((options) => {
                            if (
                                options.length > 0 &&
                                !this.riskAssessmentControl.value
                            ) {
                                this.riskAssessmentControl.setValue(
                                    options[0].value,
                                );
                                formTypeToUse = options[0].value;
                            } else {
                                formTypeToUse =
                                    this.riskAssessmentControl.value;
                            }

                            if (formTypeToUse) {
                                filterParams.set("formType", formTypeToUse);
                            }

                            return this.userService.getUserStatsBeta(
                                filterParams,
                            );
                        }),
                    );
                } else {
                    filterParams.set("formType", formTypeToUse);
                    return this.userService.getUserStatsBeta(filterParams);
                }
            }),
            shareReplay(1),
            tap((stats) => {
                this.averageScoreDataSet$ = of(
                    stats.data.averageRiskScoreOverTime?.chartData,
                );
                this.completionDataset$ = of(
                    stats.data.completionRiskRateOverTime?.chartData,
                );
            }),
        );
    }

    ngOnDestroy() {
        this.timeFrameSubscription?.unsubscribe();
    }
}
